"use client";
// Imports
import Link from "next/link";
import { useLayoutEffect, useRef, useState } from "react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { UIElement, NavIcon } from "@/components";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";

// Header Nav
export function HeaderNav({ data, user, showDates }) {
  // Variables
  const { showModeNav, outOfShowModeNav, showModeEnabled } = data;
  const nav = showModeEnabled ? showModeNav : outOfShowModeNav;
  const doChildrenExist = (item) => item?.children?.length > 0;
  const rootRef = useRef(null);
  const [menuValue, setMenuValue] = useState("");
  const [viewportLeft, setViewportLeft] = useState("50%");

  // Pathname
  const pathname = usePathname();
  // Get root path
  const rootPath = pathname.split("/")[1];

  // Check if active
  function isActive(path) {
    // convert path to slug
    path = path.toLowerCase().replace(/\s/g, "-");

    return rootPath === path;
  }

  // Keep shared viewport centred under the open trigger.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !menuValue) return;

    const sync = () => {
      const trigger = root.querySelector('button[data-state="open"]');
      if (!trigger) return;

      const rootRect = root.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      const center = triggerRect.left + triggerRect.width / 2 - rootRect.left;
      setViewportLeft(`${Math.round(center)}px`);
    };

    sync();

    window.addEventListener("resize", sync);
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(sync)
        : null;
    resizeObserver?.observe(root);

    return () => {
      window.removeEventListener("resize", sync);
      resizeObserver?.disconnect();
    };
  }, [menuValue]);

  const navItemClass = cn(
    "font-heading font-heading-sentence normal-case tracking-normal text-header-foreground",
    "transition-colors duration-200 hover:text-brand-nav-accent"
  );

  // Shared viewport restores left/right content slides. --nav-viewport-left
  // keeps that viewport centred under the open trigger for hover continuity.
  return (
    <NavigationMenu
      ref={rootRef}
      delayDuration={0}
      value={menuValue}
      onValueChange={setMenuValue}
      style={{ "--nav-viewport-left": viewportLeft }}
    >
      <NavigationMenuList className="h-11 level-4 gap-x-3 lg:gap-x-5 xl:gap-x-6 normal-case">
        {nav?.map((item, index) =>
          doChildrenExist(item) ? (
            <NavigationMenuItem key={index}>
              <NavigationMenuTrigger
                className={cn(navItemClass, "bg-transparent")}
              >
                <span
                  className={cn(
                    "level-4 font-heading font-heading-sentence normal-case",
                    isActive(item?.page?.title) && "text-brand-nav-accent"
                  )}
                >
                  {item?.page?.title}
                </span>
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[min(700px,70vw)] gap-1 overflow-hidden p-4 font-body normal-case lg:grid-cols-2">
                  {item?.children.map((child, childIndex) => (
                    <li key={childIndex}>
                      {!child?.page?.action ||
                      child?.page?.action?.length < 1 ? (
                        <Link
                          href={child?.page?.url ?? "#"}
                          target={
                            child?.page?.entry_id ? "_self" : "_blank"
                          }
                          className="group flex items-start justify-start space-x-4 rounded-lg p-4 transition-all duration-300 lg:hover:bg-brand-nav-accent lg:hover:text-white"
                        >
                          {child?.page?.icon?.value !== "none" && (
                            <div
                              className="flex size-10"
                              data-icon={child?.page?.icon?.value}
                            >
                              <NavIcon
                                className="object-cover w-full h-full"
                                icon={child?.page?.icon?.value}
                              />
                            </div>
                          )}
                          <div className="flex flex-col items-left">
                            <p className="level-4-alt font-heading normal-case">
                              {child?.page?.title}
                            </p>
                            {child?.page?.description && (
                              <p className="font-body text-sm font-normal leading-snug normal-case opacity-70">
                                {child?.page?.description}
                              </p>
                            )}
                          </div>
                        </Link>
                      ) : (
                        <UIElement
                          showDates={showDates}
                          slug={child?.page?.action[0]?.slug}
                          user={user}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
          ) : (
            <NavigationMenuItem key={index}>
              {!item.page.action || item.page.action.length < 1 ? (
                <Link
                  href={item?.page?.url ?? "#"}
                  target={item?.page?.title ? "_self" : "_blank"}
                  className={cn(
                    navItemClass,
                    "flex-none level-4",
                    isActive(item?.page?.title) && "text-brand-nav-accent"
                  )}
                >
                  {item?.page?.title}
                </Link>
              ) : (
                <UIElement
                  showDates={showDates}
                  slug={item?.page?.action[0]?.slug}
                  user={user}
                />
              )}
            </NavigationMenuItem>
          )
        )}
      </NavigationMenuList>
    </NavigationMenu>
  );
}
